import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BookOpenCheck,
  Building2,
  FileSpreadsheet,
  Landmark,
  LayoutDashboard,
  LockKeyhole,
  Plus,
  Search,
  Save,
  Settings2,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import api, { FRESH_FINANCE_SOCIETY_KEY } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Loading } from "../../components/shared";
import FreshFinanceBilling from "./FreshFinanceBilling";
import FreshFinanceReceipts from "./FreshFinanceReceipts";
import FreshFinanceReports from "./FreshFinanceReports";

const MENU = [
  {
    id: "overview",
    label: "Dashboard",
    icon: LayoutDashboard,
    available: true,
  },
  { id: "masters", label: "Units & Members", icon: Building2, available: true },
  {
    id: "billing",
    label: "Billing & Charges",
    icon: Landmark,
    available: true,
  },
  {
    id: "receipts",
    label: "Collections",
    icon: FileSpreadsheet,
    available: true,
  },
  {
    id: "accounting",
    label: "Old Data & Opening",
    icon: BookOpenCheck,
    available: true,
  },
  { id: "reports", label: "Member Ledger & Reports", icon: FileSpreadsheet, available: true },
  { id: "utilities", label: "Import & Export", icon: Settings2, available: true },
];

const emptyUnit = {
  building_id: "",
  wing_id: "",
  unit_number: "",
  floor: "",
  unit_type: "FLAT",
  carpet_area: "",
  built_up_area: "",
  custom_chargeable_area: "",
  status: "ACTIVE",
};

const emptyMember = {
  unit_id: "",
  name: "",
  member_number: "",
  member_type: "INDIVIDUAL",
  first_name: "",
  last_name: "",
  email: "",
  mobile: "",
  alternate_email: "",
  alternate_mobile: "",
  date_of_birth: "",
  occupation: "",
  share_certificate_number: "",
  correspondence_address: "",
  permanent_address: "",
  city: "",
  state: "",
  pincode: "",
  ownership_type: "OWNER",
  occupancy_status: "SELF_OCCUPIED",
  possession_date: "",
  status: "ACTIVE",
};

const inr = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function FreshFinancePreview({ adminMode = false }) {
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get("section");
  const validSection = (id) => MENU.some((item) => item.id === id);
  const [activeSection, setActiveSection] = useState(
    sectionParam && validSection(sectionParam) ? sectionParam : "overview",
  );
  useEffect(() => {
    if (sectionParam && validSection(sectionParam)) {
      setActiveSection(sectionParam);
    }
  }, [sectionParam]);
  const [activeMaster, setActiveMaster] = useState("buildings");
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({
    buildings: [],
    wings: [],
    units: [],
    members: [],
  });
  const [openings, setOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState("");
  const [access, setAccess] = useState({ permissions: [] });
  const [adminSocieties, setAdminSocieties] = useState([]);
  const [candidateQuery, setCandidateQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [candidateId, setCandidateId] = useState("");
  const [previewAcknowledged, setPreviewAcknowledged] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [selectedSociety, setSelectedSociety] = useState(
    adminMode ? localStorage.getItem(FRESH_FINANCE_SOCIETY_KEY) || "" : "",
  );

  const load = async () => {
    setLoading(true);
    try {
      const accessResponse = await api.get("/fresh-finance/access");
      const [bootstrap, buildings, wings, units, members, openingRows] =
        await Promise.all([
          api.get("/fresh-finance/bootstrap"),
          api.get("/fresh-finance/masters/buildings"),
          api.get("/fresh-finance/masters/wings"),
          api.get("/fresh-finance/masters/units"),
          api.get("/fresh-finance/masters/members"),
          api.get("/fresh-finance/opening-balances"),
        ]);
      setData(bootstrap.data);
      setRows({
        buildings: buildings.data,
        wings: wings.data,
        units: units.data.items || [],
        members: members.data.items || [],
      });
      setOpenings(openingRows.data);
      setAccess(accessResponse.data);
      setAccessError("");
    } catch (error) {
      setAccessError(
        error.response?.data?.detail ||
          "Preview access is not enabled for this user.",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadAdminSocieties = async () => {
    try {
      const response = await api.get("/fresh-finance/admin/preview-societies");
      const societies = response.data || [];
      setAdminSocieties(societies);
      return societies;
    } catch {
      setAdminSocieties([]);
      return [];
    }
  };

  useEffect(() => {
    if (adminMode) {
      (async () => {
        const societies = await loadAdminSocieties();
        const stored = localStorage.getItem(FRESH_FINANCE_SOCIETY_KEY) || "";
        if (stored && societies.some((society) => society.society_id === stored)) {
          await load();
        } else {
          localStorage.removeItem(FRESH_FINANCE_SOCIETY_KEY);
          setSelectedSociety("");
          setLoading(false);
        }
      })();
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseSociety = async (societyId) => {
    setSelectedSociety(societyId);
    if (societyId) {
      localStorage.setItem(FRESH_FINANCE_SOCIETY_KEY, societyId);
      await load();
    } else {
      localStorage.removeItem(FRESH_FINANCE_SOCIETY_KEY);
      setData(null);
    }
  };

  const searchPreviewCandidates = async () => {
    const query = candidateQuery.trim();
    if (query.length < 2) {
      toast.error("Enter at least two characters to search societies");
      return;
    }
    setPreviewBusy(true);
    try {
      const response = await api.get("/fresh-finance/admin/preview-candidates", {
        params: { q: query },
      });
      setCandidates(response.data || []);
      setCandidateId("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not search societies");
    } finally {
      setPreviewBusy(false);
    }
  };

  const enablePreviewCandidate = async () => {
    if (!candidateId || !previewAcknowledged) return;
    const candidate = candidates.find((item) => item.society_id === candidateId);
    setPreviewBusy(true);
    try {
      await api.put(`/fresh-finance/admin/preview-societies/${candidateId}`);
      await loadAdminSocieties();
      toast.success(`${candidate?.name || "Society"} is enabled for Super Admin preview`);
      await chooseSociety(candidateId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not enable preview access");
    } finally {
      setPreviewBusy(false);
    }
  };

  const disablePreviewSociety = async (society) => {
    setPreviewBusy(true);
    try {
      await api.delete(`/fresh-finance/admin/preview-societies/${society.society_id}`);
      if (selectedSociety === society.society_id) {
        localStorage.removeItem(FRESH_FINANCE_SOCIETY_KEY);
        setSelectedSociety("");
        setData(null);
      }
      await loadAdminSocieties();
      toast.success(`${society.name} preview access was disabled`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not disable preview access");
    } finally {
      setPreviewBusy(false);
    }
  };

  const activeYear = useMemo(
    () =>
      data?.financial_years?.find(
        (year) =>
          year.financial_year_id === data?.settings?.active_financial_year_id,
      ),
    [data],
  );
  const canManageMasters = access.permissions.includes("manage_masters");
  const canManageYears = access.permissions.includes("manage_financial_years");
  const canManageOpenings = access.permissions.includes(
    "manage_opening_balances",
  );
  const canImportExport = access.permissions.includes("import_export");

  const activateYear = async (financialYearId) => {
    try {
      await api.put(
        `/fresh-finance/financial-years/${financialYearId}/activate`,
      );
      toast.success("Active financial year updated");
      load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not update financial year",
      );
    }
  };

  if (loading) return <Loading />;

  if (adminMode && !selectedSociety) {
    return (
      <section
        className="mx-auto mt-10 max-w-xl border border-gray-200 bg-white p-6"
        data-testid="fresh-finance-admin-picker"
      >
        <span className="border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800">
          SUPER ADMIN PREVIEW
        </span>
        <h1 className="mt-3 font-display text-2xl font-bold">
          Select a preview society
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Fresh Finance stays hidden from normal navigation. Choose an authorized
          preview society to open its isolated accounting workspace.
        </p>
        <AdminPreviewAccessPanel
          acknowledged={previewAcknowledged}
          busy={previewBusy}
          candidateId={candidateId}
          candidates={candidates}
          enabledSocieties={adminSocieties}
          onAcknowledgedChange={setPreviewAcknowledged}
          onCandidateChange={setCandidateId}
          onDisable={disablePreviewSociety}
          onEnable={enablePreviewCandidate}
          onQueryChange={setCandidateQuery}
          onSearch={searchPreviewCandidates}
          query={candidateQuery}
        />
        <label className="mt-4 grid gap-1 text-xs font-semibold text-gray-600">
          Authorized preview society
          <select
            className="h-9 border border-gray-300 bg-white px-2 text-sm text-gray-950"
            data-testid="fresh-finance-admin-society-select"
            onChange={(event) => chooseSociety(event.target.value)}
            value={selectedSociety}
          >
            <option value="">Select a society</option>
            {adminSocieties.map((society) => (
              <option key={society.society_id} value={society.society_id}>
                {society.name}
              </option>
            ))}
          </select>
        </label>
      </section>
    );
  }

  if (accessError) {
    return (
      <section
        className="border border-amber-300 bg-amber-50 p-6"
        data-testid="fresh-finance-access-denied"
      >
        <LockKeyhole className="h-5 w-5 text-amber-700" />
        <h1 className="mt-3 font-display text-2xl font-bold">
          Society Finance access is restricted
        </h1>
        <p className="mt-2 text-sm text-amber-900">{accessError}</p>
      </section>
    );
  }

  return (
    <div
      className="min-h-[calc(100vh-7rem)] bg-[#f9fafb] text-gray-950"
      data-testid="fresh-finance-preview-page"
    >
      <header className="border border-gray-200 bg-white px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl font-bold text-[#0F2B63]">
                Society Finance &amp; Billing
              </h1>
              {adminMode && access.mode === "PREVIEW" && (
                <span
                  className="border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800"
                  data-testid="fresh-finance-preview-badge"
                >
                  SUPER ADMIN PREVIEW
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-600">
              Units, members, charges, bills, collections and ledgers in one
              society-scoped workspace.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            {adminMode && (
              <label className="grid gap-1 text-xs font-semibold text-gray-600">
                Preview society
                <select
                  className="h-8 min-w-48 border border-gray-300 bg-white px-2 text-sm text-gray-950"
                  data-testid="fresh-finance-admin-society-switch"
                  onChange={(event) => chooseSociety(event.target.value)}
                  value={selectedSociety}
                >
                  <option value="">Select a society</option>
                  {adminSocieties.map((society) => (
                    <option key={society.society_id} value={society.society_id}>
                      {society.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="grid gap-1 text-xs font-semibold text-gray-600">
              Active financial year
              <select
                className="h-8 min-w-48 border border-gray-300 bg-white px-2 text-sm text-gray-950"
                data-testid="fresh-finance-active-year"
                disabled={!canManageYears}
                onChange={(event) => activateYear(event.target.value)}
                value={activeYear?.financial_year_id || ""}
              >
                <option value="">Create a financial year</option>
                {(data?.financial_years || []).map((year) => (
                  <option
                    key={year.financial_year_id}
                    value={year.financial_year_id}
                  >
                    {year.label} · {year.status}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </header>

      <div className={`grid gap-0 border-x border-b border-gray-200 ${sectionParam ? "" : "lg:grid-cols-[220px_minmax(0,1fr)]"}`}>
        {!sectionParam && (
        <aside className="border-b border-gray-200 bg-white p-3 lg:border-b-0 lg:border-r">
          <nav className="grid gap-1" data-testid="fresh-finance-navigation">
            {MENU.map((item) => {
              const Icon = item.icon;
              const selected = activeSection === item.id;
              return (
                <button
                  className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium
                    transition-all duration-200 ${
                      selected
                        ? "bg-gradient-to-r from-[#2563EB] to-[#1d4ed8] text-white shadow-md shadow-blue-500/30"
                        : "text-slate-600 hover:bg-[#2563EB]/5 hover:text-[#0F2B63]"
                    }`}
                  data-testid={`fresh-finance-nav-${item.id}`}
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  type="button"
                >
                  <Icon className={`h-4 w-4 ${selected ? "text-white" : "text-[#2563EB]"}`} />
                  <span className="flex-1">{item.label}</span>
                  {!item.available && (
                    <span className="text-[10px]">Unavailable</span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>
        )}
        <main className="min-w-0 p-4 sm:p-5">
          {activeSection === "overview" && (
            <Overview data={data} onGo={setActiveSection} />
          )}
          {activeSection === "masters" && (
            <Masters
              activeMaster={activeMaster}
              onChangeMaster={setActiveMaster}
              onReload={load}
              rows={rows}
              canManageMasters={canManageMasters}
              canManageYears={canManageYears}
            />
          )}
          {activeSection === "accounting" && (
            <OpeningBalances
              accounts={data?.accounts || []}
              canManage={canManageOpenings}
              openings={openings}
              onReload={load}
            />
          )}
          {activeSection === "utilities" && (
            <ImportTools canImportExport={canImportExport} onReload={load} />
          )}
          {activeSection === "billing" && (
            <FreshFinanceBilling
              accounts={data?.accounts || []}
              access={access}
            />
          )}
          {activeSection === "receipts" && (
            <FreshFinanceReceipts
              accounts={data?.accounts || []}
              access={access}
            />
          )}
          {activeSection === "reports" && (
            <FreshFinanceReports accounts={data?.accounts || []} />
          )}
        </main>
      </div>
    </div>
  );
}

function AdminPreviewAccessPanel({
  acknowledged,
  busy,
  candidateId,
  candidates,
  enabledSocieties,
  onAcknowledgedChange,
  onCandidateChange,
  onDisable,
  onEnable,
  onQueryChange,
  onSearch,
  query,
}) {
  return (
    <section
      className="mt-5 border border-blue-200 bg-blue-50 p-4"
      data-testid="fresh-finance-preview-access-control"
    >
      <h2 className="font-display text-base font-bold text-blue-950">
        Authorize a restricted preview
      </h2>
      <p className="mt-1 text-xs leading-5 text-blue-900">
        This gives Super Admin access only. It does not add Fresh Finance to the
        Society Admin portal or change legacy accounting.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="grid min-w-56 gap-1 text-xs font-semibold text-gray-700">
          Find a society
          <Input
            className="h-8 border-blue-200 bg-white text-sm"
            data-testid="fresh-finance-preview-search-input"
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Type society name"
            value={query}
          />
        </label>
        <Button
          data-testid="fresh-finance-preview-search-button"
          disabled={busy}
          onClick={onSearch}
          size="sm"
          type="button"
          variant="outline"
        >
          <Search className="h-4 w-4" />
          Search
        </Button>
      </div>
      {candidates.length > 0 && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="grid gap-1 text-xs font-semibold text-gray-700">
            Society to authorize
            <select
              className="h-8 border border-blue-200 bg-white px-2 text-sm text-gray-950"
              data-testid="fresh-finance-preview-candidate-select"
              onChange={(event) => onCandidateChange(event.target.value)}
              value={candidateId}
            >
              <option value="">Select a society</option>
              {candidates.map((candidate) => (
                <option key={candidate.society_id} value={candidate.society_id}>
                  {candidate.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 self-end text-xs text-gray-700">
            <input
              checked={acknowledged}
              data-testid="fresh-finance-preview-acknowledgement"
              onChange={(event) => onAcknowledgedChange(event.target.checked)}
              type="checkbox"
            />
            Keep this society Super Admin preview-only
          </label>
        </div>
      )}
      {candidates.length === 0 && query.length >= 2 && !busy && (
        <p className="mt-3 text-xs text-amber-800" data-testid="fresh-finance-preview-no-match">
          No matching society was found.
        </p>
      )}
      <Button
        className="mt-3"
        data-testid="fresh-finance-preview-enable-button"
        disabled={!candidateId || !acknowledged || busy}
        onClick={onEnable}
        size="sm"
        type="button"
      >
        Enable Super Admin preview
      </Button>
      {enabledSocieties.length > 0 && (
        <div className="mt-5 border-t border-blue-200 pt-3">
          <p className="text-xs font-semibold uppercase text-blue-900">
            Authorized preview societies
          </p>
          <ul className="mt-2 grid gap-2">
            {enabledSocieties.map((society) => (
              <li
                className="flex flex-wrap items-center justify-between gap-2 bg-white px-3 py-2 text-sm"
                data-testid={`fresh-finance-preview-enabled-${society.society_id}`}
                key={society.society_id}
              >
                <span>{society.name}</span>
                <Button
                  data-testid={`fresh-finance-preview-disable-${society.society_id}`}
                  disabled={busy}
                  onClick={() => onDisable(society)}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  Disable preview
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Overview({ data, onGo }) {
  const summary = data?.summary || {};
  return (
    <section data-testid="fresh-finance-overview">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 pb-4">
        <div>
          <h2 className="mt-1 font-display text-2xl font-bold">
            Billing workspace
          </h2>
        </div>
        <span className="text-xs font-medium text-gray-600">
          All figures use INR
        </span>
      </div>
      <div className="mt-5 grid gap-px border border-gray-200 bg-gray-200 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ["Buildings", summary.buildings || 0],
          ["Units", summary.units || 0],
          ["Members", summary.members || 0],
          ["Posted opening entries", summary.posted_openings || 0],
        ].map(([label, value]) => (
          <div
            className="bg-white p-4"
            data-testid={`fresh-finance-kpi-${label.toLowerCase().replaceAll(" ", "-")}`}
            key={label}
          >
            <p className="text-xs font-semibold uppercase text-gray-600">
              {label}
            </p>
            <p className="mt-2 font-display text-3xl font-bold tabular-nums">
              {value}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-5 grid gap-3 border border-gray-200 bg-white p-4 md:grid-cols-3">
        <QuickAction
          label="Set up units & members"
          onClick={() => onGo("masters")}
          testId="fresh-finance-overview-masters"
        />
        <QuickAction
          label="Enter old data"
          onClick={() => onGo("accounting")}
          testId="fresh-finance-overview-openings"
        />
        <QuickAction
          label="Import units, members or old data"
          onClick={() => onGo("utilities")}
          testId="fresh-finance-overview-imports"
        />
      </div>
    </section>
  );
}

function QuickAction({ label, onClick, testId }) {
  return (
    <button
      className="border border-gray-200 px-3 py-3 text-left text-sm font-semibold transition-colors hover:border-blue-600 hover:bg-blue-50"
      data-testid={testId}
      onClick={onClick}
      type="button"
    >
      {label}
    </button>
  );
}

function Masters({
  activeMaster,
  canManageMasters,
  canManageYears,
  onChangeMaster,
  onReload,
  rows,
}) {
  const tabs = [
    ["buildings", "Property structure"],
    ["units", "Units"],
    ["relationships", "Unit relationships"],
    ["members", "Members"],
    ["accounts", "Accounts"],
    ["years", "Financial years"],
  ];
  return (
    <section data-testid="fresh-finance-masters">
      <div className="border-b border-gray-200 pb-3">
        <h2 className="mt-1 font-display text-2xl font-bold">
          Units &amp; member records
        </h2>
      </div>
      <div
        className="mt-4 flex flex-wrap border-b border-gray-200"
        data-testid="fresh-finance-master-tabs"
      >
        {tabs.map(([id, label]) => (
          <button
            className={`border-b-2 px-3 py-2 text-sm font-medium ${
              activeMaster === id
                ? "border-blue-700 text-blue-800"
                : "border-transparent text-gray-600 hover:text-gray-950"
            }`}
            data-testid={`fresh-finance-master-tab-${id}`}
            key={id}
            onClick={() => onChangeMaster(id)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>
      {!canManageMasters && (
        <p
          className="mt-4 text-sm text-gray-600"
          data-testid="fresh-finance-view-only-notice"
        >
          Your access is view-only. Editing controls are disabled.
        </p>
      )}
      {activeMaster === "buildings" && (
        <BuildingMaster
          buildings={rows.buildings}
          canManage={canManageMasters}
          onReload={onReload}
          wings={rows.wings}
        />
      )}
      {activeMaster === "units" && (
        <UnitMaster
          buildings={rows.buildings}
          canManage={canManageMasters}
          onReload={onReload}
          units={rows.units}
          wings={rows.wings}
        />
      )}
      {activeMaster === "relationships" && (
        <UnitRelationshipMaster
          canManage={canManageMasters}
          members={rows.members}
          units={rows.units}
        />
      )}
      {activeMaster === "members" && (
        <MemberMaster
          canManage={canManageMasters}
          members={rows.members}
          onReload={onReload}
          units={rows.units}
        />
      )}
      {activeMaster === "accounts" && (
        <AccountMaster canManage={canManageMasters} onReload={onReload} />
      )}
      {activeMaster === "years" && (
        <FinancialYearMaster canManage={canManageYears} onReload={onReload} />
      )}
    </section>
  );
}

function BuildingMaster({ buildings, canManage, onReload, wings }) {
  const [building, setBuilding] = useState({ name: "", code: "" });
  const [wing, setWing] = useState({ building_id: "", name: "" });
  const submit = async (path, payload, reset, message) => {
    try {
      await api.post(path, payload);
      toast.success(message);
      reset();
      onReload();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not save master record",
      );
    }
  };
  return (
    <div
      className="mt-4 space-y-5"
      data-testid="fresh-finance-buildings-master"
    >
      <QuickForm
        disabled={!canManage}
        title="Add building"
        testId="fresh-finance-building-form"
      >
        <CompactInput
          label="Building name"
          testId="fresh-finance-building-name"
          value={building.name}
          onChange={(value) => setBuilding({ ...building, name: value })}
        />
        <CompactInput
          label="Code"
          testId="fresh-finance-building-code"
          value={building.code}
          onChange={(value) => setBuilding({ ...building, code: value })}
        />
        <SaveButton
          testId="fresh-finance-building-save"
          onClick={() =>
            submit(
              "/fresh-finance/masters/buildings",
              building,
              () => setBuilding({ name: "", code: "" }),
              "Building added",
            )
          }
        />
      </QuickForm>
      <QuickForm
        disabled={!canManage}
        title="Add wing"
        testId="fresh-finance-wing-form"
      >
        <CompactSelect
          label="Building"
          testId="fresh-finance-wing-building"
          value={wing.building_id}
          onChange={(value) => setWing({ ...wing, building_id: value })}
          options={buildings.map((item) => [item.building_id, item.name])}
        />
        <CompactInput
          label="Wing name"
          testId="fresh-finance-wing-name"
          value={wing.name}
          onChange={(value) => setWing({ ...wing, name: value })}
        />
        <SaveButton
          testId="fresh-finance-wing-save"
          onClick={() =>
            submit(
              "/fresh-finance/masters/wings",
              wing,
              () => setWing({ building_id: "", name: "" }),
              "Wing added",
            )
          }
        />
      </QuickForm>
      <DenseTable
        headers={["Building", "Code", "Wings"]}
        testId="fresh-finance-buildings-table"
      >
        {buildings.map((item) => (
          <tr key={item.building_id}>
            <td className="px-3 py-2 font-medium">{item.name}</td>
            <td className="px-3 py-2">{item.code || "—"}</td>
            <td className="px-3 py-2 tabular-nums">
              {
                wings.filter(
                  (wingItem) => wingItem.building_id === item.building_id,
                ).length
              }
            </td>
          </tr>
        ))}
      </DenseTable>
    </div>
  );
}

function UnitMaster({ buildings, canManage, onReload, units, wings }) {
  const [form, setForm] = useState(emptyUnit);
  const [editing, setEditing] = useState(null);
  const relevantWings = wings.filter(
    (item) => item.building_id === form.building_id,
  );
  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/fresh-finance/masters/units/${editing}`, form);
        toast.success("Unit updated");
      } else {
        await api.post("/fresh-finance/masters/units", form);
        toast.success("Unit added");
      }
      setForm(emptyUnit);
      setEditing(null);
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save unit");
    }
  };
  const beginEdit = (unit) => {
    setEditing(unit.unit_id);
    setForm({ ...emptyUnit, ...unit });
  };
  return (
    <div className="mt-4 space-y-5" data-testid="fresh-finance-units-master">
      <QuickForm
        disabled={!canManage}
        title={editing ? "Edit unit" : "Add unit"}
        testId="fresh-finance-unit-form"
      >
        <CompactSelect
          label="Building"
          testId="fresh-finance-unit-building"
          value={form.building_id}
          onChange={(value) =>
            setForm({ ...form, building_id: value, wing_id: "" })
          }
          options={buildings.map((item) => [item.building_id, item.name])}
        />
        <CompactSelect
          label="Wing"
          testId="fresh-finance-unit-wing"
          value={form.wing_id}
          onChange={(value) => setForm({ ...form, wing_id: value })}
          options={relevantWings.map((item) => [item.wing_id, item.name])}
          allowBlank
        />
        <CompactInput
          label="Unit no."
          testId="fresh-finance-unit-number"
          value={form.unit_number}
          onChange={(value) => setForm({ ...form, unit_number: value })}
        />
        <CompactInput
          label="Floor"
          testId="fresh-finance-unit-floor"
          value={form.floor}
          onChange={(value) => setForm({ ...form, floor: value })}
        />
        <CompactInput
          label="Carpet area"
          testId="fresh-finance-unit-carpet-area"
          value={form.carpet_area}
          onChange={(value) => setForm({ ...form, carpet_area: value })}
        />
        <SaveButton testId="fresh-finance-unit-save" onClick={save} />
        {editing && (
          <Button
            data-testid="fresh-finance-unit-cancel"
            onClick={() => {
              setEditing(null);
              setForm(emptyUnit);
            }}
            size="sm"
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
        )}
      </QuickForm>
      <DenseTable
        headers={["Unit", "Building", "Wing", "Floor", "Carpet", "Status", ""]}
        testId="fresh-finance-units-table"
      >
        {units.map((unit) => (
          <tr key={unit.unit_id}>
            <td className="px-3 py-2 font-medium">{unit.unit_number}</td>
            <td className="px-3 py-2">{unit.building_name}</td>
            <td className="px-3 py-2">{unit.wing_name || "—"}</td>
            <td className="px-3 py-2">{unit.floor || "—"}</td>
            <td className="px-3 py-2 text-right tabular-nums">
              {unit.carpet_area || "—"}
            </td>
            <td className="px-3 py-2">{unit.status}</td>
            <td className="px-3 py-2 text-right">
              <button
                className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                data-testid={`fresh-finance-unit-edit-${unit.unit_id}`}
                disabled={!canManage}
                onClick={() => beginEdit(unit)}
                type="button"
              >
                Edit
              </button>
            </td>
          </tr>
        ))}
      </DenseTable>
    </div>
  );
}

function UnitRelationshipMaster({ canManage, members, units }) {
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [relationships, setRelationships] = useState([]);
  const [form, setForm] = useState({
    member_id: "",
    role: "OWNER",
    send_setup_email: false,
  });
  const [busy, setBusy] = useState(false);
  const selectedMember = members.find((member) => member.member_id === form.member_id);

  useEffect(() => {
    if (!selectedUnitId && units.length) {
      setSelectedUnitId(units[0].unit_id);
    }
  }, [selectedUnitId, units]);

  const loadRelationships = async (unitId = selectedUnitId) => {
    if (!unitId) {
      setRelationships([]);
      return;
    }
    try {
      const response = await api.get("/fresh-finance/unit-relationships", {
        params: { unit_id: unitId },
      });
      setRelationships(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load unit relationships");
    }
  };

  useEffect(() => {
    loadRelationships();
  }, [selectedUnitId]);

  const selectUnit = (unitId) => {
    setSelectedUnitId(unitId);
    setForm({ member_id: "", role: "OWNER", send_setup_email: false });
  };

  const assign = async () => {
    try {
      setBusy(true);
      const response = await api.post(
        `/fresh-finance/units/${selectedUnitId}/relationships`,
        form,
      );
      const status = response.data.setup_email?.status;
      toast.success(
        status === "SENT"
          ? "Member assigned and secure setup email sent"
          : "Member assigned to this unit",
      );
      setForm({ member_id: "", role: "OWNER", send_setup_email: false });
      await loadRelationships();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not assign the member");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (relationship) => {
    try {
      setBusy(true);
      await api.delete(
        `/fresh-finance/units/${selectedUnitId}/relationships/${relationship.relationship_id}`,
      );
      toast.success("Unit relationship removed");
      await loadRelationships();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove the relationship");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 space-y-5" data-testid="fresh-finance-unit-relationships">
      <section className="border border-gray-200 bg-white p-4">
        <label className="grid max-w-sm gap-1 text-sm font-medium text-gray-800">
          Select unit
          <select
            className="h-9 border border-gray-300 bg-white px-2 text-sm"
            data-testid="fresh-finance-relationship-unit"
            onChange={(event) => selectUnit(event.target.value)}
            value={selectedUnitId}
          >
            <option value="">Select unit</option>
            {units.map((unit) => (
              <option key={unit.unit_id} value={unit.unit_id}>
                {unit.unit_number}
              </option>
            ))}
          </select>
        </label>
      </section>
      <QuickForm
        disabled={!canManage || !selectedUnitId}
        title="Assign member to selected unit"
        testId="fresh-finance-relationship-form"
      >
        <CompactSelect
          label="Member"
          testId="fresh-finance-relationship-member"
          value={form.member_id}
          onChange={(value) =>
            setForm({ ...form, member_id: value, send_setup_email: false })
          }
          options={members.map((member) => [
            member.member_id,
            `${member.name}${member.email ? ` · ${member.email}` : ""}`,
          ])}
        />
        <CompactSelect
          label="Relationship"
          testId="fresh-finance-relationship-role"
          value={form.role}
          onChange={(value) => setForm({ ...form, role: value })}
          options={[
            ["OWNER", "Owner"],
            ["CO_OWNER", "Co-owner"],
            ["TENANT", "Tenant"],
            ["NOMINEE", "Nominee"],
          ]}
        />
        {selectedMember?.email && (
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              checked={form.send_setup_email}
              data-testid="fresh-finance-relationship-send-setup"
              onChange={(event) =>
                setForm({ ...form, send_setup_email: event.target.checked })
              }
              type="checkbox"
            />
            Send secure account setup email
          </label>
        )}
        <Button
          data-testid="fresh-finance-relationship-assign"
          disabled={!form.member_id || busy}
          onClick={assign}
          size="sm"
          type="button"
        >
          Assign member
        </Button>
      </QuickForm>
      <DenseTable
        headers={["Relationship", "Member", "Email", ""]}
        testId="fresh-finance-relationships-table"
      >
        {relationships.length === 0 && (
          <tr>
            <td className="px-3 py-4 text-sm text-gray-500" colSpan={4}>
              No active members are assigned to this unit.
            </td>
          </tr>
        )}
        {relationships.map((relationship) => (
          <tr key={relationship.relationship_id}>
            <td className="px-3 py-2 font-medium">
              {relationship.role.replaceAll("_", " ")}
            </td>
            <td className="px-3 py-2">{relationship.member?.name || "Former member"}</td>
            <td className="px-3 py-2">{relationship.member?.email || "—"}</td>
            <td className="px-3 py-2 text-right">
              <button
                className="text-xs font-semibold text-rose-700 hover:text-rose-900"
                data-testid={`fresh-finance-relationship-remove-${relationship.relationship_id}`}
                disabled={!canManage || busy}
                onClick={() => remove(relationship)}
                type="button"
              >
                Remove
              </button>
            </td>
          </tr>
        ))}
      </DenseTable>
    </div>
  );
}

function MemberMaster({ canManage, members, onReload, units }) {
  const [form, setForm] = useState(emptyMember);
  const [editing, setEditing] = useState(null);
  const [member360, setMember360] = useState(null);
  const [transferMember, setTransferMember] = useState(null);
  const [creatingLogin, setCreatingLogin] = useState("");
  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/fresh-finance/masters/members/${editing}`, form);
        toast.success("Member updated");
      } else {
        await api.post("/fresh-finance/masters/members", form);
        toast.success("Member added");
      }
      setForm(emptyMember);
      setEditing(null);
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save member");
    }
  };
  const open360 = async (memberId) => {
    try {
      const response = await api.get(`/fresh-finance/masters/members/${memberId}/360`);
      setMember360(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load member details");
    }
  };
  const createResidentLogin = async (member) => {
    try {
      setCreatingLogin(member.member_id);
      const response = await api.post("/resident-admin/direct-logins", {
        person_id: `fresh_finance:${member.member_id}`,
      });
      toast.success(
        response.data.created
          ? "Resident login created. A one-time password setup email was sent."
          : "Resident password setup email was resent.",
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create the resident login");
    } finally {
      setCreatingLogin("");
    }
  };
  return (
    <div className="mt-4 space-y-5" data-testid="fresh-finance-members-master">
      <QuickForm
        disabled={!canManage}
        title={editing ? "Edit member" : "Add member"}
        testId="fresh-finance-member-form"
      >
        <CompactSelect
          label="Unit"
          testId="fresh-finance-member-unit"
          value={form.unit_id}
          onChange={(value) => setForm({ ...form, unit_id: value })}
          options={units.map((item) => [item.unit_id, item.unit_number])}
        />
        <CompactInput
          label="Member registration no."
          testId="fresh-finance-member-number"
          value={form.member_number}
          onChange={(value) => setForm({ ...form, member_number: value })}
        />
        <CompactSelect
          label="Member type"
          testId="fresh-finance-member-type"
          value={form.member_type}
          onChange={(value) => setForm({ ...form, member_type: value })}
          options={[
            ["INDIVIDUAL", "Individual"],
            ["ORGANIZATION", "Organization"],
          ]}
        />
        <CompactInput
          label="Member name"
          testId="fresh-finance-member-name"
          value={form.name}
          onChange={(value) => setForm({ ...form, name: value })}
        />
        <CompactInput
          label="First name"
          testId="fresh-finance-member-first-name"
          value={form.first_name}
          onChange={(value) => setForm({ ...form, first_name: value })}
        />
        <CompactInput
          label="Last name"
          testId="fresh-finance-member-last-name"
          value={form.last_name}
          onChange={(value) => setForm({ ...form, last_name: value })}
        />
        <CompactInput
          label="Email"
          testId="fresh-finance-member-email"
          value={form.email}
          onChange={(value) => setForm({ ...form, email: value })}
        />
        <CompactInput
          label="Mobile"
          testId="fresh-finance-member-mobile"
          value={form.mobile}
          onChange={(value) => setForm({ ...form, mobile: value })}
        />
        <CompactInput
          label="Alternate email"
          testId="fresh-finance-member-alternate-email"
          value={form.alternate_email}
          onChange={(value) => setForm({ ...form, alternate_email: value })}
        />
        <CompactInput
          label="Alternate mobile"
          testId="fresh-finance-member-alternate-mobile"
          value={form.alternate_mobile}
          onChange={(value) => setForm({ ...form, alternate_mobile: value })}
        />
        <CompactInput
          label="Date of birth"
          testId="fresh-finance-member-date-of-birth"
          type="date"
          value={form.date_of_birth}
          onChange={(value) => setForm({ ...form, date_of_birth: value })}
        />
        <CompactInput
          label="Occupation"
          testId="fresh-finance-member-occupation"
          value={form.occupation}
          onChange={(value) => setForm({ ...form, occupation: value })}
        />
        <CompactInput
          label="Share certificate no."
          testId="fresh-finance-member-share-certificate"
          value={form.share_certificate_number}
          onChange={(value) => setForm({ ...form, share_certificate_number: value })}
        />
        <CompactInput
          label="Correspondence address"
          testId="fresh-finance-member-correspondence-address"
          value={form.correspondence_address}
          onChange={(value) => setForm({ ...form, correspondence_address: value })}
        />
        <CompactInput
          label="Permanent address"
          testId="fresh-finance-member-permanent-address"
          value={form.permanent_address}
          onChange={(value) => setForm({ ...form, permanent_address: value })}
        />
        <CompactInput
          label="City"
          testId="fresh-finance-member-city"
          value={form.city}
          onChange={(value) => setForm({ ...form, city: value })}
        />
        <CompactInput
          label="State"
          testId="fresh-finance-member-state"
          value={form.state}
          onChange={(value) => setForm({ ...form, state: value })}
        />
        <CompactInput
          label="Pincode"
          testId="fresh-finance-member-pincode"
          value={form.pincode}
          onChange={(value) => setForm({ ...form, pincode: value })}
        />
        <SaveButton testId="fresh-finance-member-save" onClick={save} />
        {editing && (
          <Button
            data-testid="fresh-finance-member-cancel"
            onClick={() => {
              setEditing(null);
              setForm(emptyMember);
            }}
            size="sm"
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
        )}
      </QuickForm>
      <DenseTable
        headers={["Member", "Registration", "Unit", "Email", "Mobile", "Occupancy", ""]}
        testId="fresh-finance-members-table"
      >
        {members.map((member) => (
          <tr key={member.member_id}>
            <td className="px-3 py-2 font-medium">{member.name}</td>
            <td className="px-3 py-2">{member.member_number || "—"}</td>
            <td className="px-3 py-2">{member.unit_number}</td>
            <td className="px-3 py-2">{member.email || "—"}</td>
            <td className="px-3 py-2 tabular-nums">{member.mobile || "—"}</td>
            <td className="px-3 py-2">{member.occupancy_status}</td>
            <td className="px-3 py-2 text-right">
              <div className="flex justify-end gap-2">
                <button
                  className="text-xs font-semibold text-slate-700 hover:text-slate-950"
                  data-testid={`fresh-finance-member-360-${member.member_id}`}
                  onClick={() => open360(member.member_id)}
                  type="button"
                >
                  360°
                </button>
                <button
                  className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                  data-testid={`fresh-finance-member-edit-${member.member_id}`}
                  disabled={!canManage}
                  onClick={() => {
                    setEditing(member.member_id);
                    setForm({ ...emptyMember, ...member });
                  }}
                  type="button"
                >
                  Edit
                </button>
                <button
                  className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                  data-testid={`fresh-finance-member-transfer-${member.member_id}`}
                  disabled={!canManage}
                  onClick={() => setTransferMember(member)}
                  type="button"
                >
                  Transfer
                </button>
                <button
                  className="text-xs font-semibold text-violet-700 hover:text-violet-900"
                  data-testid={`fresh-finance-member-create-login-${member.member_id}`}
                  disabled={!canManage || !member.email || creatingLogin === member.member_id}
                  onClick={() => createResidentLogin(member)}
                  type="button"
                >
                  {creatingLogin === member.member_id ? "Creating…" : "Create login"}
                </button>
              </div>
            </td>
          </tr>
        ))}
      </DenseTable>
      {member360 && (
        <Member360Dialog member360={member360} onClose={() => setMember360(null)} />
      )}
      {transferMember && (
        <MemberTransferDialog
          member={transferMember}
          onClose={() => setTransferMember(null)}
          onDone={() => {
            setTransferMember(null);
            onReload();
          }}
          units={units}
        />
      )}
    </div>
  );
}

function Member360Dialog({ member360, onClose }) {
  const {
    bills = [],
    member = {},
    receipts = [],
    transfer_history: transfers = [],
    unit = {},
  } = member360;
  const [emailing, setEmailing] = useState(false);
  const emailLedger = async () => {
    try {
      setEmailing(true);
      const response = await api.post(
        `/fresh-finance/masters/members/${member.member_id}/ledger/email`,
      );
      toast.success(
        response.data.status === "ALREADY_SENT"
          ? "This ledger was already emailed"
          : "Ledger emailed to the member",
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not email the member ledger");
    } finally {
      setEmailing(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4"
      data-testid="fresh-finance-member-360-dialog"
    >
      <section className="mx-auto my-8 w-full max-w-4xl border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 pb-3">
          <div>
            <p className="text-xs font-semibold uppercase text-blue-700">Member 360°</p>
            <h3 className="font-display text-xl font-bold">{member.name}</h3>
            <p className="text-sm text-gray-600">
              Unit {unit.unit_number} · {member.email || "No email"}
            </p>
          </div>
          <Button
            data-testid="fresh-finance-member-360-close"
            onClick={onClose}
            size="sm"
            type="button"
            variant="outline"
          >
            Close
          </Button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <Metric label="Bills" value={bills.length} />
          <Metric label="Receipts" value={receipts.length} />
          <Metric label="Transfers" value={transfers.length} />
        </div>
        <Button
          className="mt-4"
          data-testid="fresh-finance-member-360-email-ledger"
          disabled={!member.email || emailing}
          onClick={emailLedger}
          size="sm"
          type="button"
        >
          Email member ledger
        </Button>
        <DenseTable
          headers={["Bill no.", "Period", "Status", "Total"]}
          testId="fresh-finance-member-360-bills"
        >
          {bills.slice(0, 8).map((bill) => (
            <tr key={bill.bill_id}>
              <td className="px-3 py-2 font-mono text-xs">{bill.bill_number || "Draft"}</td>
              <td className="px-3 py-2">{bill.period_snapshot?.label}</td>
              <td className="px-3 py-2">{bill.status}</td>
              <td className="px-3 py-2 text-right tabular-nums">{inr(bill.grand_total)}</td>
            </tr>
          ))}
        </DenseTable>
      </section>
    </div>
  );
}

function MemberTransferDialog({ member, onClose, onDone, units }) {
  const [targetUnitId, setTargetUnitId] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const transfer = async () => {
    try {
      setBusy(true);
      await api.post(`/fresh-finance/masters/members/${member.member_id}/transfer`, {
        target_unit_id: targetUnitId,
        effective_date: effectiveDate,
        note,
      });
      toast.success("Member transferred. Historical bills remain with the original unit snapshot.");
      onDone();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not transfer member");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="fresh-finance-member-transfer-dialog"
    >
      <section className="w-full max-w-lg border border-gray-200 bg-white p-5">
        <h3 className="font-display text-lg font-bold">Transfer {member.name}</h3>
        <p className="mt-1 text-sm text-gray-600">Move the active member record to a vacant unit.</p>
        <div className="mt-4 grid gap-3">
          <CompactSelect
            label="Target unit"
            testId="fresh-finance-transfer-target-unit"
            value={targetUnitId}
            onChange={setTargetUnitId}
            options={units
              .filter((unit) => unit.unit_id !== member.unit_id)
              .map((unit) => [unit.unit_id, unit.unit_number])}
          />
          <CompactInput
            label="Effective date"
            testId="fresh-finance-transfer-date"
            type="date"
            value={effectiveDate}
            onChange={setEffectiveDate}
          />
          <CompactInput
            label="Transfer note"
            testId="fresh-finance-transfer-note"
            value={note}
            onChange={setNote}
          />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button
            data-testid="fresh-finance-transfer-cancel"
            onClick={onClose}
            size="sm"
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            data-testid="fresh-finance-transfer-confirm"
            disabled={!targetUnitId || busy}
            onClick={transfer}
            size="sm"
            type="button"
          >
            Transfer member
          </Button>
        </div>
      </section>
    </div>
  );
}

function AccountMaster({ canManage, onReload }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    code: "",
    name: "",
    account_type: "ASSET",
  });
  const load = async () => {
    const response = await api.get("/fresh-finance/accounts");
    setAccounts(response.data);
  };
  useEffect(() => {
    load();
  }, []);
  const save = async () => {
    try {
      await api.post("/fresh-finance/accounts", form);
      toast.success("Account added");
      setForm({ code: "", name: "", account_type: "ASSET" });
      load();
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add account");
    }
  };
  return (
    <div className="mt-4 space-y-5" data-testid="fresh-finance-accounts-master">
      <QuickForm
        disabled={!canManage}
        title="Add account"
        testId="fresh-finance-account-form"
      >
        <CompactInput
          label="Code"
          testId="fresh-finance-account-code"
          value={form.code}
          onChange={(value) => setForm({ ...form, code: value })}
        />
        <CompactInput
          label="Account name"
          testId="fresh-finance-account-name"
          value={form.name}
          onChange={(value) => setForm({ ...form, name: value })}
        />
        <CompactSelect
          label="Type"
          testId="fresh-finance-account-type"
          value={form.account_type}
          onChange={(value) => setForm({ ...form, account_type: value })}
          options={[
            ["ASSET", "Asset"],
            ["LIABILITY", "Liability"],
            ["EQUITY", "Equity"],
            ["INCOME", "Income"],
            ["EXPENSE", "Expense"],
          ]}
        />
        <SaveButton testId="fresh-finance-account-save" onClick={save} />
      </QuickForm>
      <DenseTable
        headers={["Code", "Account", "Type", "Origin"]}
        testId="fresh-finance-accounts-table"
      >
        {accounts.map((account) => (
          <tr key={account.account_id}>
            <td className="px-3 py-2 font-mono tabular-nums">{account.code}</td>
            <td className="px-3 py-2 font-medium">{account.name}</td>
            <td className="px-3 py-2">{account.account_type}</td>
            <td className="px-3 py-2">
              {account.is_system ? "System" : "Custom"}
            </td>
          </tr>
        ))}
      </DenseTable>
    </div>
  );
}

function FinancialYearMaster({ canManage, onReload }) {
  const [years, setYears] = useState([]);
  const [form, setForm] = useState({ label: "", start_date: "", end_date: "" });
  const load = async () => {
    const response = await api.get("/fresh-finance/financial-years");
    setYears(response.data);
  };
  useEffect(() => {
    load();
  }, []);
  const save = async () => {
    try {
      await api.post("/fresh-finance/financial-years", form);
      toast.success("Financial year created");
      setForm({ label: "", start_date: "", end_date: "" });
      load();
      onReload();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not create financial year",
      );
    }
  };
  return (
    <div className="mt-4 space-y-5" data-testid="fresh-finance-years-master">
      <QuickForm
        disabled={!canManage}
        title="Add financial year"
        testId="fresh-finance-year-form"
      >
        <CompactInput
          label="Label"
          testId="fresh-finance-year-label"
          value={form.label}
          onChange={(value) => setForm({ ...form, label: value })}
        />
        <CompactInput
          label="Start"
          testId="fresh-finance-year-start"
          type="date"
          value={form.start_date}
          onChange={(value) => setForm({ ...form, start_date: value })}
        />
        <CompactInput
          label="End"
          testId="fresh-finance-year-end"
          type="date"
          value={form.end_date}
          onChange={(value) => setForm({ ...form, end_date: value })}
        />
        <SaveButton testId="fresh-finance-year-save" onClick={save} />
      </QuickForm>
      <DenseTable
        headers={["Financial year", "Start", "End", "Status"]}
        testId="fresh-finance-years-table"
      >
        {years.map((year) => (
          <tr key={year.financial_year_id}>
            <td className="px-3 py-2 font-medium">{year.label}</td>
            <td className="px-3 py-2 tabular-nums">{year.start_date}</td>
            <td className="px-3 py-2 tabular-nums">{year.end_date}</td>
            <td className="px-3 py-2">{year.status}</td>
          </tr>
        ))}
      </DenseTable>
    </div>
  );
}

function OpeningBalances({ accounts, canManage, openings, onReload }) {
  const [form, setForm] = useState({
    posting_date: new Date().toISOString().slice(0, 10),
    narration: "Opening balance",
    idempotency_key: `${Date.now()}-opening`,
    lines: [
      { account_id: "", debit: "", credit: "" },
      { account_id: "", debit: "", credit: "" },
    ],
  });
  const totals = form.lines.reduce(
    (sum, line) => ({
      debit: sum.debit + Number(line.debit || 0),
      credit: sum.credit + Number(line.credit || 0),
    }),
    { debit: 0, credit: 0 },
  );
  const updateLine = (index, field, value) =>
    setForm({
      ...form,
      lines: form.lines.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [field]: value } : line,
      ),
    });
  const post = async () => {
    try {
      await api.post("/fresh-finance/opening-balances", {
        ...form,
        lines: form.lines.map((line) => ({
          ...line,
          debit: Number(line.debit || 0),
          credit: Number(line.credit || 0),
        })),
      });
      toast.success("Balanced opening entry posted");
      onReload();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Opening entry could not be posted",
      );
    }
  };
  return (
    <section data-testid="fresh-finance-openings">
      <div className="border-b border-gray-200 pb-3">
        <p className="text-xs font-semibold uppercase text-blue-700">
          Accounting foundation
        </p>
        <h2 className="mt-1 font-display text-2xl font-bold">
          Opening balances
        </h2>
      </div>
      <div className="mt-4 border border-gray-200 bg-white p-4">
        <fieldset className="contents" disabled={!canManage}>
          <div className="grid gap-3 md:grid-cols-3">
            <CompactInput
              label="Posting date"
              testId="fresh-finance-opening-date"
              type="date"
              value={form.posting_date}
              onChange={(value) => setForm({ ...form, posting_date: value })}
            />
            <CompactInput
              label="Narration"
              testId="fresh-finance-opening-narration"
              value={form.narration}
              onChange={(value) => setForm({ ...form, narration: value })}
            />
            <CompactInput
              label="Idempotency reference"
              testId="fresh-finance-opening-reference"
              value={form.idempotency_key}
              onChange={(value) => setForm({ ...form, idempotency_key: value })}
            />
          </div>
          <div className="mt-4 overflow-x-auto border border-gray-200">
            <table className="w-full min-w-[620px] text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">Account</th>
                  <th className="px-3 py-2 text-right">Debit</th>
                  <th className="px-3 py-2 text-right">Credit</th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line, index) => (
                  <tr className="border-t" key={index}>
                    <td className="px-3 py-1">
                      <select
                        className="h-8 w-full border border-gray-300 bg-white px-2"
                        data-testid={`fresh-finance-opening-account-${index}`}
                        onChange={(event) =>
                          updateLine(index, "account_id", event.target.value)
                        }
                        value={line.account_id}
                      >
                        <option value="">Select account</option>
                        {accounts.map((account) => (
                          <option
                            key={account.account_id}
                            label={`${account.code} · ${account.name}`}
                            value={account.account_id}
                          />
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-1">
                      <Input
                        className="h-8 text-right tabular-nums"
                        data-testid={`fresh-finance-opening-debit-${index}`}
                        onChange={(event) =>
                          updateLine(index, "debit", event.target.value)
                        }
                        value={line.debit}
                      />
                    </td>
                    <td className="px-3 py-1">
                      <Input
                        className="h-8 text-right tabular-nums"
                        data-testid={`fresh-finance-opening-credit-${index}`}
                        onChange={(event) =>
                          updateLine(index, "credit", event.target.value)
                        }
                        value={line.credit}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t bg-gray-50 font-semibold">
                <tr>
                  <td className="px-3 py-2 text-right">Totals</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {inr(totals.debit)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {inr(totals.credit)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              data-testid="fresh-finance-opening-add-line"
              onClick={() =>
                setForm({
                  ...form,
                  lines: [
                    ...form.lines,
                    { account_id: "", debit: "", credit: "" },
                  ],
                })
              }
              type="button"
              variant="outline"
            >
              <Plus className="h-4 w-4" />
              Add line
            </Button>
            <Button
              data-testid="fresh-finance-opening-post"
              onClick={post}
              type="button"
            >
              <Save className="h-4 w-4" />
              Post balanced entry
            </Button>
          </div>
        </fieldset>
      </div>
      <DenseTable
        headers={["Date", "Narration", "Debit", "Credit", "Status"]}
        testId="fresh-finance-openings-table"
      >
        {openings.map((entry) => (
          <tr key={entry.journal_id}>
            <td className="px-3 py-2 tabular-nums">{entry.posting_date}</td>
            <td className="px-3 py-2">{entry.narration}</td>
            <td className="px-3 py-2 text-right tabular-nums">
              {inr(entry.total_debit)}
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
              {inr(entry.total_credit)}
            </td>
            <td className="px-3 py-2">{entry.status}</td>
          </tr>
        ))}
      </DenseTable>
    </section>
  );
}

function ImportTools({ canImportExport, onReload }) {
  const inputRef = useRef(null);
  const [type, setType] = useState("units");
  const [preview, setPreview] = useState(null);
  const download = async (kind) => {
    try {
      const response = await api.get(`/fresh-finance/imports/${type}/${kind}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `fresh-finance-${type}-${kind}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download Excel file");
    }
  };
  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const response = await api.post(
        `/fresh-finance/imports/${type}/preview`,
        form,
      );
      setPreview(response.data);
      toast.success("Import preview ready");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not validate workbook",
      );
    }
    event.target.value = "";
  };
  const commit = async () => {
    try {
      const response = await api.post(
        `/fresh-finance/imports/${preview.import_job_id}/commit`,
        { mode: "UPSERT" },
      );
      toast.success(
        `${response.data.created} created · ${response.data.updated} updated`,
      );
      setPreview(null);
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not commit import");
    }
  };
  return (
    <section data-testid="fresh-finance-utilities">
      <div className="border-b border-gray-200 pb-3">
        <p className="text-xs font-semibold uppercase text-blue-700">
          Utilities
        </p>
        <h2 className="mt-1 font-display text-2xl font-bold">
          Excel import and export
        </h2>
      </div>
      <div className="mt-4 border border-gray-200 bg-white p-4">
        <fieldset className="contents" disabled={!canImportExport}>
          <div className="flex flex-wrap items-end gap-3">
            <CompactSelect
              label="Master"
              testId="fresh-finance-import-type"
              value={type}
              onChange={(value) => {
                setType(value);
                setPreview(null);
              }}
              options={[
                ["units", "Units"],
                ["members", "Members"],
              ]}
            />
            <Button
              data-testid="fresh-finance-template-download"
              onClick={() => download("template")}
              type="button"
              variant="outline"
            >
              Download template
            </Button>
            <Button
              data-testid="fresh-finance-export-download"
              onClick={() => download("export")}
              type="button"
              variant="outline"
            >
              Export current
            </Button>
            <input
              accept=".xlsx"
              data-testid="fresh-finance-import-file"
              hidden
              onChange={upload}
              ref={inputRef}
              type="file"
            />
            <Button
              data-testid="fresh-finance-import-select"
              onClick={() => inputRef.current?.click()}
              type="button"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Preview import
            </Button>
          </div>
          {preview && (
            <div
              className="mt-5 border border-gray-200 bg-gray-50 p-4"
              data-testid="fresh-finance-import-preview"
            >
              <div className="grid gap-3 sm:grid-cols-4">
                <Metric label="Rows" value={preview.summary.total_rows} />
                <Metric label="Valid" value={preview.summary.valid_rows} />
                <Metric label="New" value={preview.summary.new_rows} />
                <Metric label="Errors" value={preview.summary.error_rows} />
              </div>
              {preview.errors.length > 0 ? (
                <ul
                  className="mt-4 list-disc pl-5 text-sm text-red-700"
                  data-testid="fresh-finance-import-errors"
                >
                  {preview.errors.map((error) => (
                    <li key={error.row_number}>
                      Row {error.row_number}: {error.errors.join(", ")}
                    </li>
                  ))}
                </ul>
              ) : (
                <Button
                  className="mt-4"
                  data-testid="fresh-finance-import-commit"
                  onClick={commit}
                  type="button"
                >
                  Commit validated import
                </Button>
              )}
            </div>
          )}
        </fieldset>
      </div>
    </section>
  );
}

function Unavailable({ module }) {
  return (
    <section
      className="border border-dashed border-gray-300 bg-white p-8"
      data-testid="fresh-finance-unavailable"
    >
      <LockKeyhole className="h-5 w-5 text-gray-500" />
      <h2 className="mt-3 font-display text-xl font-bold">
        {module} is unavailable in Phase A
      </h2>
      <p className="mt-2 max-w-xl text-sm text-gray-600">
        The isolated foundation is active for preview testing only. This module
        will be implemented in a later approved phase.
      </p>
    </section>
  );
}
function Metric({ label, value }) {
  return (
    <div
      className="border border-gray-200 bg-white p-3"
      data-testid={`fresh-finance-import-metric-${label.toLowerCase()}`}
    >
      <p className="text-xs uppercase text-gray-600">{label}</p>
      <p className="mt-1 font-display text-xl font-bold tabular-nums">
        {value}
      </p>
    </div>
  );
}
function QuickForm({ children, disabled = false, title, testId }) {
  return (
    <section
      className="border border-gray-200 bg-white p-4"
      data-testid={testId}
    >
      <h3 className="font-display text-sm font-bold">{title}</h3>
      <fieldset
        className="mt-3 flex flex-wrap items-end gap-3 disabled:opacity-50"
        disabled={disabled}
      >
        {children}
      </fieldset>
    </section>
  );
}
function CompactInput({ label, onChange, testId, type = "text", value }) {
  return (
    <label className="grid min-w-36 gap-1 text-xs font-semibold text-gray-600">
      <Label>{label}</Label>
      <Input
        className="h-8 border-gray-300 bg-white text-sm"
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        value={value}
      />
    </label>
  );
}
function CompactSelect({
  allowBlank = false,
  label,
  onChange,
  options,
  testId,
  value,
}) {
  return (
    <label className="grid min-w-36 gap-1 text-xs font-semibold text-gray-600">
      <Label>{label}</Label>
      <select
        className="h-8 border border-gray-300 bg-white px-2 text-sm text-gray-950"
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      >
        {allowBlank && <option value="">No wing</option>}
        <option value="">Select</option>
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}
function SaveButton({ onClick, testId }) {
  return (
    <Button data-testid={testId} onClick={onClick} size="sm" type="button">
      <Save className="h-4 w-4" />
      Save
    </Button>
  );
}
function DenseTable({ children, headers, testId }) {
  return (
    <div
      className="overflow-x-auto border border-gray-200"
      data-testid={testId}
    >
      <table className="w-full min-w-[620px] text-sm">
        <thead className="sticky top-0 bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
          <tr>
            {headers.map((header) => (
              <th className="px-3 py-2" key={header}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">{children}</tbody>
      </table>
    </div>
  );
}
